import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Modal } from './Modal';
import { Button } from './Button';
import { Input } from './Input';
import { dataCorrectionsAPI } from '../../api/endpoints';
import type { Venue, DataCorrectionType, InvalidField } from '../../types';
import { CircleAlert, ShieldAlert, Clock, Ban } from 'lucide-react';

const CORRECTION_TYPES: { value: DataCorrectionType; label: string; description: string; icon: typeof CircleAlert }[] = [
  { value: 'wrong', label: 'Wrong Information', description: 'Some fields have incorrect data', icon: CircleAlert },
  { value: 'spam', label: 'Spam', description: 'This venue is spam or not a real venue', icon: ShieldAlert },
  { value: 'expired', label: 'Expired / Closed', description: 'This venue no longer exists or is permanently closed', icon: Clock },
  { value: 'inappropriate', label: 'Inappropriate', description: 'This venue contains inappropriate content', icon: Ban },
];

const REPORTABLE_FIELDS: { key: keyof Venue; label: string }[] = [
  { key: 'name', label: 'Name' },
  { key: 'url', label: 'Website' },
  { key: 'type', label: 'Type' },
  { key: 'address', label: 'Address' },
  { key: 'styles', label: 'Styles' },
  { key: 'status', label: 'Status' },
];

interface ReportMistakeModalProps {
  isOpen: boolean;
  onClose: () => void;
  venue: Venue | null;
}

export function ReportMistakeModal({ isOpen, onClose, venue }: ReportMistakeModalProps) {
  const [correctionType, setCorrectionType] = useState<DataCorrectionType>('wrong');
  const [precisions, setPrecisions] = useState('');
  const [invalidFields, setInvalidFields] = useState<InvalidField[]>([]);

  const mutation = useMutation({
    mutationFn: dataCorrectionsAPI.create,
    onSuccess: () => {
      handleClose();
    },
  });

  const handleClose = () => {
    setCorrectionType('wrong');
    setPrecisions('');
    setInvalidFields([]);
    mutation.reset();
    onClose();
  };

  const toggleField = (fieldKey: string) => {
    setInvalidFields((prev) => {
      const existing = prev.find((f) => f.field === fieldKey);
      if (existing) {
        return prev.filter((f) => f.field !== fieldKey);
      }
      const currentValue = venue ? getFieldDisplayValue(venue, fieldKey) : null;
      return [...prev, { field: fieldKey, current_value: currentValue, suggested_value: null }];
    });
  };

  const updateSuggestedValue = (fieldKey: string, value: string) => {
    setInvalidFields((prev) =>
      prev.map((f) => (f.field === fieldKey ? { ...f, suggested_value: value || null } : f)),
    );
  };

  const handleSubmit = () => {
    if (!venue) return;
    mutation.mutate({
      data_type: 'venue_core',
      object_id: venue._id,
      type: correctionType,
      precisions: precisions || null,
      invalid_fields: invalidFields,
    });
  };

  if (!venue) return null;

  const showFieldSelection = correctionType === 'wrong';

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Report a Mistake — ${venue.name}`}
      size="lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={mutation.isPending || (showFieldSelection && invalidFields.length === 0 && !precisions)}
          >
            {mutation.isPending ? 'Submitting...' : 'Submit Report'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {mutation.isSuccess && (
          <div className="p-3 bg-green-900/30 border border-green-700/50 rounded-lg text-green-300 text-sm">
            Report submitted successfully. Thank you!
          </div>
        )}

        {mutation.isError && (
          <div className="p-3 bg-red-900/30 border border-red-700/50 rounded-lg text-red-300 text-sm">
            Failed to submit report. Please try again.
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-dark-text-secondary mb-2">
            What's wrong?
          </label>
          <div className="grid grid-cols-2 gap-2">
            {CORRECTION_TYPES.map((ct) => {
              const Icon = ct.icon;
              return (
              <button
                key={ct.value}
                onClick={() => setCorrectionType(ct.value)}
                className={`p-3 rounded-lg border text-left transition-colors ${
                  correctionType === ct.value
                    ? 'border-primary-500 bg-primary-900/30 text-primary-300'
                    : 'border-dark-border bg-dark-elevated text-dark-text-secondary hover:border-dark-border-hover'
                }`}
              >
                <div className="flex items-center gap-1.5 text-sm font-medium">
                  <Icon className="w-4 h-4 shrink-0" />
                  {ct.label}
                </div>
                <div className="text-xs mt-0.5 opacity-70">{ct.description}</div>
              </button>
              );
            })}
          </div>
        </div>

        {showFieldSelection && (
          <div>
            <label className="block text-sm font-medium text-dark-text-secondary mb-2">
              Which fields are incorrect?
            </label>
            <div className="space-y-2">
              {REPORTABLE_FIELDS.map((field) => {
                const isSelected = invalidFields.some((f) => f.field === field.key);
                const currentValue = getFieldDisplayValue(venue, field.key);
                return (
                  <div key={field.key}>
                    <button
                      onClick={() => toggleField(field.key)}
                      className={`w-full p-2 rounded-lg border text-left text-sm transition-colors ${
                        isSelected
                          ? 'border-primary-500 bg-primary-900/30 text-primary-300'
                          : 'border-dark-border bg-dark-elevated text-dark-text-secondary hover:border-dark-border-hover'
                      }`}
                    >
                      <span className="font-medium">{field.label}</span>
                      {currentValue && (
                        <span className="ml-2 opacity-60">— {currentValue}</span>
                      )}
                    </button>
                    {isSelected && (
                      <div className="mt-1 ml-4">
                        <Input
                          placeholder="Suggested correction (optional)"
                          value={invalidFields.find((f) => f.field === field.key)?.suggested_value || ''}
                          onChange={(e) => updateSuggestedValue(field.key, e.target.value)}
                          className="text-sm"
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-dark-text-secondary mb-1">
            Additional details (optional)
          </label>
          <textarea
            value={precisions}
            onChange={(e) => setPrecisions(e.target.value)}
            placeholder="Any extra context about the issue..."
            className="input w-full min-h-[80px] resize-y"
          />
        </div>
      </div>
    </Modal>
  );
}

function getFieldDisplayValue(venue: Venue, fieldKey: string): string | null {
  switch (fieldKey) {
    case 'name':
      return venue.name;
    case 'url':
      return venue.url || null;
    case 'type':
      return venue.type;
    case 'address': {
      const a = venue.address;
      if (!a) return null;
      return [a.street, a.city, a.administrative_region_1, a.country].filter(Boolean).join(', ');
    }
    case 'styles':
      return venue.styles.length > 0 ? venue.styles.join(', ') : null;
    case 'status':
      return venue.status;
    default:
      return null;
  }
}
